/**
 * Webtech HR Engine™ - SuperAdmin Billing Dashboard
 * Phase 2: Gestion des abonnements, paiements et facturation
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  CreditCard,
  Receipt,
  Tag,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Filter,
  MoreVertical,
  Gift,
  Percent,
  FileText,
  Download,
  RefreshCw,
  Plus,
  Edit,
  Eye,
  Calendar,
  Building2,
  Users,
  Crown,
  Loader2,
  ArrowUpDown,
  ChevronRight
} from 'lucide-react';

// Formatage monétaire F CFA
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0) + ' F CFA';
};

// Formatage date
const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return dateString;
  }
};

// Status Badge pour abonnements
function SubscriptionStatusBadge({ status }) {
  const statusConfig = {
    active: { label: 'Actif', class: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    grace_period: { label: 'Délai de grâce', class: 'bg-amber-100 text-amber-700', icon: Clock },
    suspended: { label: 'Suspendu', class: 'bg-red-100 text-red-700', icon: XCircle },
    cancelled: { label: 'Annulé', class: 'bg-slate-100 text-slate-700', icon: XCircle },
    pending: { label: 'En attente', class: 'bg-blue-100 text-blue-700', icon: Clock }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge className={`${config.class} font-medium`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

// Status Badge pour paiements
function PaymentStatusBadge({ status }) {
  const statusConfig = {
    completed: { label: 'Complété', class: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700' },
    failed: { label: 'Échoué', class: 'bg-red-100 text-red-700' },
    refunded: { label: 'Remboursé', class: 'bg-purple-100 text-purple-700' },
    cancelled: { label: 'Annulé', class: 'bg-slate-100 text-slate-700' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Badge className={`${config.class} font-medium`}>
      {config.label}
    </Badge>
  );
}

// Status Badge pour factures
function InvoiceStatusBadge({ status }) {
  const statusConfig = {
    paid: { label: 'Payée', class: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'En attente', class: 'bg-amber-100 text-amber-700' },
    overdue: { label: 'En retard', class: 'bg-red-100 text-red-700' },
    failed: { label: 'Échouée', class: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Annulée', class: 'bg-slate-100 text-slate-700' },
    draft: { label: 'Brouillon', class: 'bg-slate-100 text-slate-700' }
  };

  const config = statusConfig[status] || statusConfig.pending;

  return (
    <Badge className={`${config.class} font-medium`}>
      {config.label}
    </Badge>
  );
}

// Dialog pour offrir des mois gratuits
function GiftMonthsDialog({ isOpen, onClose, subscription, onSuccess }) {
  const [months, setMonths] = useState(1);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/superadmin/subscriptions/${subscription.id}/gift`, {
        months,
        reason
      });
      toast.success(`${months} mois offerts avec succès`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'opération");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-purple-500" />
            Offrir des mois gratuits
          </DialogTitle>
          <DialogDescription>
            {subscription?.company?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Nombre de mois</Label>
            <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 6, 12].map(m => (
                  <SelectItem key={m} value={String(m)}>{m} mois</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Raison *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Compensation, fidélisation, promotion..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Offrir {months} mois
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog pour appliquer une remise
function ApplyDiscountDialog({ isOpen, onClose, subscription, onSuccess }) {
  const [discountPercent, setDiscountPercent] = useState(10);
  const [durationMonths, setDurationMonths] = useState(3);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/superadmin/subscriptions/${subscription.id}/discount`, {
        discountPercent,
        durationMonths,
        reason
      });
      toast.success(`Remise de ${discountPercent}% appliquée`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'opération");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-amber-500" />
            Appliquer une remise
          </DialogTitle>
          <DialogDescription>
            {subscription?.company?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Remise (%)</Label>
              <Select value={String(discountPercent)} onValueChange={(v) => setDiscountPercent(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 15, 20, 25, 30, 50].map(p => (
                    <SelectItem key={p} value={String(p)}>{p}%</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Durée (mois)</Label>
              <Select value={String(durationMonths)} onValueChange={(v) => setDurationMonths(Number(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 3, 6, 12].map(m => (
                    <SelectItem key={m} value={String(m)}>{m} mois</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Raison *</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Négociation commerciale, fidélité..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Appliquer -{discountPercent}%
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog pour créer une facture manuelle
function CreateInvoiceDialog({ isOpen, onClose, onSuccess }) {
  const [companyId, setCompanyId] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchCompanies();
    }
  }, [isOpen]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/superadmin/companies');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const handleSubmit = async () => {
    if (!companyId || !description || !amount) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await api.post('/superadmin/invoices/manual', {
        companyId,
        description,
        amount: parseInt(amount),
        notes
      });
      toast.success('Facture créée avec succès');
      onSuccess();
      onClose();
      // Reset form
      setCompanyId('');
      setDescription('');
      setAmount('');
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-500" />
            Créer une facture manuelle
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Entreprise *</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une entreprise" />
              </SelectTrigger>
              <SelectContent>
                {companies.map(company => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Description *</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Services supplémentaires, Formation..."
            />
          </div>

          <div className="space-y-2">
            <Label>Montant (F CFA) *</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Ex: 50000"
            />
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes internes..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-webtech-blue hover:bg-webtech-blue-hover"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Créer la facture
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog pour créer un code promo
function CreatePromoCodeDialog({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    code: '',
    type: 'percentage',
    value: 10,
    scope: 'first_payment',
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    maxUses: '',
    maxUsesPerCompany: 1,
    minUsers: 10,
    description: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!formData.code || !formData.validUntil) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      await api.post('/superadmin/promo-codes', {
        ...formData,
        maxUses: formData.maxUses ? parseInt(formData.maxUses) : null
      });
      toast.success('Code promo créé avec succès');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag className="w-5 h-5 text-green-500" />
            Créer un code promo
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Code *</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({...formData, code: e.target.value.toUpperCase()})}
                placeholder="Ex: PROMO2024"
              />
            </div>

            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={(v) => setFormData({...formData, type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Pourcentage (%)</SelectItem>
                  <SelectItem value="fixed">Montant fixe (F CFA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valeur</Label>
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => setFormData({...formData, value: parseInt(e.target.value) || 0})}
              />
            </div>

            <div className="space-y-2">
              <Label>Portée</Label>
              <Select 
                value={formData.scope} 
                onValueChange={(v) => setFormData({...formData, scope: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_payment">1er paiement</SelectItem>
                  <SelectItem value="all_payments">Tous les paiements</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Valide du *</Label>
              <Input
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({...formData, validFrom: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label>Valide jusqu'au *</Label>
              <Input
                type="date"
                value={formData.validUntil}
                onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Utilisations max</Label>
              <Input
                type="number"
                value={formData.maxUses}
                onChange={(e) => setFormData({...formData, maxUses: e.target.value})}
                placeholder="Illimité"
              />
            </div>

            <div className="space-y-2">
              <Label>Min. utilisateurs</Label>
              <Input
                type="number"
                value={formData.minUsers}
                onChange={(e) => setFormData({...formData, minUsers: parseInt(e.target.value) || 0})}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Description interne du code promo..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className="bg-green-600 hover:bg-green-700"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Créer le code
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Composant principal
export default function SuperAdminBillingDashboard() {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [subscriptions, setSubscriptions] = useState({ subscriptions: [], stats: {} });
  const [payments, setPayments] = useState({ payments: [], stats: {} });
  const [invoices, setInvoices] = useState({ invoices: [], stats: {} });
  const [promoCodes, setPromoCodes] = useState({ promoCodes: [], stats: {} });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Dialog states
  const [giftDialog, setGiftDialog] = useState({ open: false, subscription: null });
  const [discountDialog, setDiscountDialog] = useState({ open: false, subscription: null });
  const [createInvoiceDialog, setCreateInvoiceDialog] = useState(false);
  const [createPromoDialog, setCreatePromoDialog] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, paymentsRes, invoicesRes, promosRes] = await Promise.all([
        api.get('/superadmin/subscriptions'),
        api.get('/superadmin/payments?limit=50'),
        api.get('/superadmin/invoices?limit=50'),
        api.get('/superadmin/promo-codes')
      ]);
      
      setSubscriptions(subsRes.data);
      setPayments(paymentsRes.data);
      setInvoices(invoicesRes.data);
      setPromoCodes(promosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter subscriptions
  const filteredSubscriptions = subscriptions.subscriptions?.filter(sub => {
    const matchesSearch = !searchTerm || 
      sub.company?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.admin?.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    const matchesPlan = planFilter === 'all' || sub.plan === planFilter;
    return matchesSearch && matchesStatus && matchesPlan;
  }) || [];

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">MRR Total</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(subscriptions.stats?.totalMrr)}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Abonnements PRO</p>
                <p className="text-2xl font-bold text-slate-900">
                  {subscriptions.stats?.byPlan?.pro || 0}
                </p>
                <p className="text-xs text-slate-500">
                  {subscriptions.stats?.byPlan?.free || 0} gratuits
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl">
                <Crown className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Paiements Réussis</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(payments.stats?.totalCompleted)}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl">
                <CreditCard className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Codes Promo Actifs</p>
                <p className="text-2xl font-bold text-slate-900">
                  {promoCodes.stats?.active || 0}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl">
                <Tag className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="subscriptions" className="data-[state=active]:bg-white">
              <CreditCard className="w-4 h-4 mr-2" />
              Abonnements
            </TabsTrigger>
            <TabsTrigger value="payments" className="data-[state=active]:bg-white">
              <DollarSign className="w-4 h-4 mr-2" />
              Paiements
            </TabsTrigger>
            <TabsTrigger value="invoices" className="data-[state=active]:bg-white">
              <Receipt className="w-4 h-4 mr-2" />
              Factures
            </TabsTrigger>
            <TabsTrigger value="promo-codes" className="data-[state=active]:bg-white">
              <Tag className="w-4 h-4 mr-2" />
              Codes Promo
            </TabsTrigger>
          </TabsList>

          <Button variant="outline" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher par entreprise ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[180px]">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les statuts</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="grace_period">Délai de grâce</SelectItem>
                    <SelectItem value="suspended">Suspendu</SelectItem>
                    <SelectItem value="cancelled">Annulé</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={planFilter} onValueChange={setPlanFilter}>
                  <SelectTrigger className="w-[150px]">
                    <Crown className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="free">Gratuit</SelectItem>
                    <SelectItem value="pro">BUSINESS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Subscriptions List */}
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full" />)}
                </div>
              ) : filteredSubscriptions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun abonnement trouvé</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSubscriptions.map(sub => (
                    <div 
                      key={sub.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-webtech-blue to-blue-700 rounded-xl flex items-center justify-center text-white font-bold">
                          {sub.company?.name?.charAt(0) || 'E'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-slate-900">{sub.company?.name || 'N/A'}</h3>
                            <SubscriptionStatusBadge status={sub.status} />
                            {sub.plan === 'pro' && (
                              <Badge className="bg-amber-100 text-amber-700">
                                <Crown className="w-3 h-3 mr-1" />
                                PRO
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {sub.userCount || 0} utilisateurs
                            </span>
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              MRR: {formatCurrency(sub.mrr)}
                            </span>
                            {sub.daysUntilBilling !== null && sub.daysUntilBilling !== undefined && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Prochain paiement: {sub.daysUntilBilling}j
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {sub.discountPercent > 0 && (
                          <Badge className="bg-purple-100 text-purple-700">
                            -{sub.discountPercent}%
                          </Badge>
                        )}
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setGiftDialog({ open: true, subscription: sub })}>
                              <Gift className="w-4 h-4 mr-2 text-purple-500" />
                              Offrir des mois
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDiscountDialog({ open: true, subscription: sub })}>
                              <Percent className="w-4 h-4 mr-2 text-amber-500" />
                              Appliquer une remise
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir les détails
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Historique des Paiements</CardTitle>
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    {payments.stats?.byStatus?.completed || 0} complétés
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-700">
                    {payments.stats?.byStatus?.pending || 0} en attente
                  </Badge>
                  <Badge className="bg-red-100 text-red-700">
                    {payments.stats?.byStatus?.failed || 0} échoués
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : payments.payments?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <DollarSign className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun paiement</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {payments.payments?.map(payment => (
                    <div 
                      key={payment.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <PaymentStatusBadge status={payment.status} />
                        <div>
                          <p className="font-medium text-slate-900">{payment.companyName}</p>
                          <p className="text-xs text-slate-500">
                            {payment.invoiceNumber && `Facture: ${payment.invoiceNumber}`}
                            {' • '}{formatDate(payment.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-slate-900">{formatCurrency(payment.amount)}</p>
                        {payment.paymentMethod && (
                          <p className="text-xs text-slate-500">{payment.paymentMethod}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab */}
        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Factures</CardTitle>
                <Button onClick={() => setCreateInvoiceDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Facture manuelle
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : invoices.invoices?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucune facture</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {invoices.invoices?.map(invoice => (
                    <div 
                      key={invoice.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <InvoiceStatusBadge status={invoice.status} />
                        <div>
                          <p className="font-medium text-slate-900">
                            {invoice.invoiceNumber}
                            {invoice.isManual && (
                              <Badge variant="outline" className="ml-2 text-xs">Manuel</Badge>
                            )}
                          </p>
                          <p className="text-xs text-slate-500">
                            {invoice.companyName} • {formatDate(invoice.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{formatCurrency(invoice.totalAmount)}</p>
                        </div>
                        <Button variant="ghost" size="icon">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Promo Codes Tab */}
        <TabsContent value="promo-codes" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Codes Promotionnels</CardTitle>
                <Button onClick={() => setCreatePromoDialog(true)} className="bg-green-600 hover:bg-green-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau code
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : promoCodes.promoCodes?.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Tag className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun code promo</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {promoCodes.promoCodes?.map(code => (
                    <div 
                      key={code.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`px-3 py-2 rounded-lg font-mono font-bold ${
                          code.isActive && !code.isExpired 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-slate-200 text-slate-500'
                        }`}>
                          {code.code}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">
                            {code.type === 'percentage' ? `${code.value}%` : formatCurrency(code.value)}
                            <span className="text-slate-500 font-normal ml-2">
                              {code.scope === 'first_payment' ? '(1er paiement)' : '(Tous paiements)'}
                            </span>
                          </p>
                          <p className="text-xs text-slate-500">
                            Utilisations: {code.usageRate} • 
                            Expire: {formatDate(code.validUntil)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {code.isExpired ? (
                          <Badge className="bg-slate-100 text-slate-500">Expiré</Badge>
                        ) : code.isActive ? (
                          <Badge className="bg-green-100 text-green-700">Actif</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-500">Inactif</Badge>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Edit className="w-4 h-4 mr-2" />
                              {code.isActive ? 'Désactiver' : 'Activer'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <GiftMonthsDialog
        isOpen={giftDialog.open}
        onClose={() => setGiftDialog({ open: false, subscription: null })}
        subscription={giftDialog.subscription}
        onSuccess={fetchData}
      />

      <ApplyDiscountDialog
        isOpen={discountDialog.open}
        onClose={() => setDiscountDialog({ open: false, subscription: null })}
        subscription={discountDialog.subscription}
        onSuccess={fetchData}
      />

      <CreateInvoiceDialog
        isOpen={createInvoiceDialog}
        onClose={() => setCreateInvoiceDialog(false)}
        onSuccess={fetchData}
      />

      <CreatePromoCodeDialog
        isOpen={createPromoDialog}
        onClose={() => setCreatePromoDialog(false)}
        onSuccess={fetchData}
      />
    </div>
  );
}
